// Widget projesinde src/App.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import JobWidget from "./components/JobWidget";
import api, { getMwRoot } from "./lib/api";
import { bearerSocketAuth } from "../../utils/socketAuth";
import { emitJobsSettled, settledEntries } from "../../utils/jobSettled";
import { isMotionAutoApply } from "../../utils/motionAutoApply";
import { useTranslation } from "./i18n";
import { jobDisplayName } from "./lib/jobDisplay";
import { buildMockJobs, buildMockHistoryJobs } from "./lib/mockJobs";
import { HostBridgeContext, type HostBridge } from "./hostBridge";
import { applySessionJobUpdate, applySessionJobCompleted, normalizeJob } from "./lib/jobUpdates";
import type { Job } from "./types/job";

interface AppProps extends HostBridge {
  trainingId: string;
  getToken: () => string | null;
  openSignal?: number;
  // hideFab: standalone parity default (false) — true when the host provides
  // its own launcher (ChatPanel) so the tray only opens via openSignal.
  hideFab?: boolean;
}

export default function App({
  trainingId,
  getToken,
  openSignal,
  hideFab = false,
  onJobApproved,
  onAddToScene,
  onJobCompleted,
}: AppProps) {
  const token = getToken();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [historyJobs, setHistoryJobs] = useState<Job[]>([]);
  const [mockMode, setMockMode] = useState<boolean>(false);
  const realJobsBackupRef = useRef<{ jobs: Job[]; historyJobs: Job[] } | null>(null);
  const jobsRef = useRef<Job[]>([]);
  const historyJobsRef = useRef<Job[]>([]);
  const mockModeRef = useRef<boolean>(false);

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);
  useEffect(() => {
    historyJobsRef.current = historyJobs;
  }, [historyJobs]);
  useEffect(() => {
    mockModeRef.current = mockMode;
  }, [mockMode]);

  // The socket handlers below are bound once per trainingId, so closing over
  // the prop directly would freeze the first render's handler. Same reason the
  // job lists above are mirrored into refs.
  const onJobCompletedRef = useRef<((completion: any) => void) | undefined>(undefined);
  useEffect(() => {
    onJobCompletedRef.current = onJobCompleted;
  }, [onJobCompleted]);

  // Small toasts next to the tray: every live job end (done / failed), and editor messages
  // (window "aidea:toast" { message, kind }), e.g. Sync with AI finishing.
  const { t, i18n } = useTranslation();
  const [toasts, setToasts] = useState<{ id: number; text: string; kind: string }[]>([]);
  const pushToast = useCallback((text: string, kind = "success") => {
    if (!text) return;
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev.slice(-2), { id, text, kind }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 4500);
  }, []);
  const toastedRef = useRef<Set<string>>(new Set());
  const appliedIdsRef = useRef<Set<string>>(new Set());
  // bound once by the socket handlers: always the latest t / labels
  const jobEndToastRef = useRef<((u: any) => void) | undefined>(undefined);
  jobEndToastRef.current = (u: any) => {
    const id = u?.jobId || u?.id;
    const status = String(u?.status || "").toLowerCase();
    const failed = status === "failed";
    if (!id || !(failed || status === "inspect" || status === "completed")) return;
    const type = u.jobType || u.type || "";
    // the editor toasts its own Sync with AI jobs; video-director / subtitles never show here
    if (toastedRef.current.has(id) || isMotionAutoApply(id)) return;
    if (type === "video-director" || /^subtitle[-_]generation$/.test(type)) return;
    toastedRef.current.add(id);
    const name = jobDisplayName({ type, name: u.name, metadata: u.metadata } as Job, t, i18n);
    pushToast(t(failed ? "toast.failed" : "toast.done", { name }), failed ? "error" : "success");
  };

  // Kapanmış (onaylı/reddedilmiş/silinmiş) işleri editöre duyur — sonucu
  // kendi localStorage'ında saklayan modallar (ImageAnimatorModal) bayat
  // "Onayla" çubuğunu göstermesin. Sunucu listesi geldiğinde de çalışır: başka
  // sekmede ya da modalın kendi approveJob'uyla onaylanmış işler de temizlenir.
  // Yalnız YENİ kapanışlar yayınlanır — liste her ilerleme tick'inde yenilenir,
  // her seferinde yüzlerce eski işi yeniden duyurmak dinleyicilerin
  // localStorage taramasını boşuna tetiklerdi.
  const announcedSettledRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (mockMode) return;
    const fresh = [...settledEntries(jobs), ...settledEntries(historyJobs)].filter(
      (e) => announcedSettledRef.current.get(e.id) !== e.outcome
    );
    fresh.forEach((e) => announcedSettledRef.current.set(e.id, e.outcome));
    emitJobsSettled(fresh);
  }, [jobs, historyJobs, mockMode]);

  const resetMockJobs = useCallback(() => {
    setJobs(buildMockJobs());
    setHistoryJobs(buildMockHistoryJobs());
  }, []);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const socketRef = useRef<Socket | null>(null);
  const previousTrainingIdRef = useRef<string>(""); // önceki trainingId'yi tut

  const refetchTimerRef = useRef<number | null>(null);

  const refetchHistory = useCallback(async () => {
    if (!trainingId) {
      return;
    }

    const res = await api.get<Job[]>("/history", {
      params: {
        trainingId,
      },
    });

    const jobsData = Array.isArray(res.data)
      ? res.data
      : ((res.data as any)?.jobs ?? (res.data as any)?.data ?? []);
    // Same normalization as socket updates: job.error must be a string.
    setJobs(jobsData.map(normalizeJob));
  }, [trainingId]);

  const fetchAllHistory = useCallback(async () => {
    if (!trainingId) {
      return;
    }

    try {
      const res = await api.get<Job[]>("/history/all", {
        params: {
          trainingId,
        },
      });
      const historyData = Array.isArray(res.data)
        ? res.data
        : ((res.data as any)?.jobs ?? (res.data as any)?.data ?? []);
      setHistoryJobs(historyData.map(normalizeJob));
    } catch (error) {
      console.error("[App] Failed to fetch all history:", error);
    }
  }, [trainingId]);

  const debouncedRefetchHistory = useCallback(() => {
    if (refetchTimerRef.current) window.clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = window.setTimeout(() => {
      refetchHistory();
      refetchTimerRef.current = null;
    }, 400);
  }, [refetchHistory]);

  useEffect(() => {
    if (!token) return;
    refetchHistory();
    fetchAllHistory();
  }, [token, refetchHistory, fetchAllHistory]);

  // Ctrl/Cmd + M ile mock modu aç/kapat
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!((e.ctrlKey || e.metaKey) && (e.key === "m" || e.key === "M"))) return;
      e.preventDefault();

      if (!mockModeRef.current) {
        // Mock'a geç: gerçek jobları yedekle, mock'u yükle
        realJobsBackupRef.current = {
          jobs: jobsRef.current,
          historyJobs: historyJobsRef.current,
        };
        setJobs(buildMockJobs());
        setHistoryJobs(buildMockHistoryJobs());
        setMockMode(true);
      } else {
        // Mock'tan çık: yedeği geri yükle
        const backup = realJobsBackupRef.current;
        setJobs(backup?.jobs ?? []);
        setHistoryJobs(backup?.historyJobs ?? []);
        realJobsBackupRef.current = null;
        setMockMode(false);
        // Token varsa API'den de tazele
        refetchHistory().catch(() => {});
        fetchAllHistory().catch(() => {});
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [refetchHistory, fetchAllHistory]);

  // ✅ trainingId değiştiğinde Socket.IO'yu yeniden bağla
  useEffect(() => {
    if (!trainingId) {
      return;
    }

    let cancelled = false;
    let pollTimer: number | null = null;
    let socket: Socket | null = null;

    const startSocket = (freshToken: string) => {
      // ✅ trainingId değişmediyse ve socket zaten bağlıysa yeniden bağlanma
      if (previousTrainingIdRef.current === trainingId && socketRef.current?.connected) {
        return;
      }

      previousTrainingIdRef.current = trainingId;

      socketRef.current?.disconnect();
      socketRef.current = null;

      socket = io(getMwRoot(), {
        transports: ["websocket", "polling"],
        autoConnect: true,
        // Function form: re-read on every reconnect, so a token renewed while
        // the tray is open is presented instead of the one it connected with.
        auth: bearerSocketAuth(() => getToken() || freshToken),
      });

      socketRef.current = socket;

      socket.on("connect", () => {
        socket!.emit("join_session", { trainingId });
        console.log("[Widget] Socket connected:", socket!.id);
      });

      socket.on("session_joined", () => {
        // Session joined successfully
      });

      // Surface silent WS failures (room-join denial / connection reject) — without
      // these the socket fails quietly and the widget just stops auto-updating.
      socket.on("session_error", (e) => console.error("[Widget] session_error", e));
      socket.on("connect_error", (e) =>
        console.error("[Widget] connect_error", (e as any)?.message)
      );

      socket.on("disconnect", (r) => console.log("[Widget] Socket disconnected:", r));

      socket.on("session_job_update", (update) => {
        // already placed by the editor (aidea:jobApplied): a late tick never brings the card back
        if (appliedIdsRef.current.has(update?.jobId)) return;
        setJobs((prev) => {
          const { jobs: nextJobs, unknownJob } = applySessionJobUpdate(prev, update);
          if (unknownJob) debouncedRefetchHistory();
          return nextJobs;
        });
        jobEndToastRef.current?.(update);
      });

      socket.on("session_job_completed", (completion) => {
        // Host first: types that fill an already-placed box (avatar-animate)
        // apply themselves here instead of waiting for an Approve click.
        try {
          onJobCompletedRef.current?.(completion);
        } catch (e) {
          console.error("[Widget] onJobCompleted handler failed", e);
        }
        setJobs((prev) => {
          const { jobs: nextJobs, unknownJob } = applySessionJobCompleted(prev, completion);
          if (unknownJob) debouncedRefetchHistory();
          return nextJobs;
        });
        const c = completion?.data ?? completion ?? {};
        jobEndToastRef.current?.({ ...c, status: c.status || "completed" });
      });
    };

    // Refresh-safe token wait: on a hard reload getToken() can briefly return
    // empty before auth state settles — never connect unauthenticated, poll
    // (bounded) instead of capturing an empty token once and staying stuck.
    const tryConnect = (attemptsLeft: number) => {
      if (cancelled) return;
      const freshToken = getToken();
      if (freshToken) {
        startSocket(freshToken);
        return;
      }
      if (attemptsLeft <= 0) return;
      pollTimer = window.setTimeout(() => tryConnect(attemptsLeft - 1), 500);
    };

    tryConnect(60);

    return () => {
      cancelled = true;
      if (pollTimer) {
        window.clearTimeout(pollTimer);
        pollTimer = null;
      }
      if (socket) {
        try {
          socket.emit("leave_session", { trainingId });
        } catch {}
        socket.disconnect();
      }
      if (refetchTimerRef.current) {
        window.clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, [trainingId, debouncedRefetchHistory, getToken]);

  const handleApprove = useCallback(
    async (job: Job) => {
      // İnfografik sahne/görsel onayı editörde job.result'tan uygulanır
      // (applyInfographicSceneResult / applyInfographicImagesResult). Canlı
      // socket'ten ('session_job_update' → 'inspect') gelen job yalnızca
      // metadata taşır, tam result payload'unu DEĞİL — bu yüzden eksikse
      // modal ile aynı endpoint'ten (GET /api/jobs/:id) tam result'u çek.
      let jobToApprove = job;
      // History'den (reload sonrası) onaylarken/yeniden eklerken job yalnızca
      // metadata taşır, tam result payload'unu DEĞİL — image-editing için görsel
      // URL'i, extract-text için textBoxes[], infografik için sahne payload'u
      // burada olur. Result yoksa "yeniden ekle" editöre boş job gönderir ve
      // hiçbir şey uygulanmaz. Bu yüzden result eksik olan HER tipte modal ile
      // aynı endpoint'ten (GET /api/jobs/:id) tam result'u çek.
      const needsFullResult = !job.result;
      if (needsFullResult && token) {
        try {
          // getToken() at request time — `token` is from App's last render and
          // goes stale while the tray sits idle past the access token's life.
          const res = await fetch(`${getMwRoot()}/api/jobs/${job.id}`, {
            headers: { Authorization: `Bearer ${getToken() || token}`, Accept: "application/json" },
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.result) jobToApprove = { ...job, result: data.result };
          }
        } catch (err) {
          console.warn("[App] approve: full-result fetch failed", err);
        }
      }

      const approvedJob: Job = {
        ...jobToApprove,
        approved: true,
        status: "approved",
        updatedAt: new Date().toISOString(),
      };
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      // History'de zaten varsa (history'den "yeniden ekle") YERİNDE güncelle —
      // başa taşıma. Başa alıp sonra refetchHistory sunucu sırasını (createdAt)
      // geri getirince job önce en üste zıplayıp tekrar eski yerine dönüyordu.
      // Yalnızca ilk kez history'e girerken (aktif→history) başa ekle.
      setHistoryJobs((prev) =>
        prev.some((j) => j.id === job.id)
          ? prev.map((j) => (j.id === job.id ? approvedJob : j))
          : [approvedJob, ...prev]
      );

      // Host'a bildir — İnfografik TEK görsel (infographic-image-generate) dahil
      // tüm onaylar buradan gider; editör (JobWidgetHost) job.type'a göre görseli
      // sahnenin tamamına (tam sığdır / 100%×100%) yerleştirir.
      onJobApproved(jobToApprove);
      emitJobsSettled([job.id], "approved");

      if (!token) return;
      try {
        await api.post(`/${job.id}/approve`);
        await refetchHistory();
        await fetchAllHistory();
      } catch (err) {
        console.warn("[App] approve API failed (mock mode):", err);
      }
    },
    [token, onJobApproved, refetchHistory, fetchAllHistory]
  );

  // The editor placed a job's result itself (Sync with AI): record it as approved — history,
  // server approval — without calling the host again and without ever offering Approve.
  const markApplied = useCallback(
    (jobId: string) => {
      if (!jobId) return;
      appliedIdsRef.current.add(jobId);
      const known =
        jobsRef.current.find((j) => j.id === jobId) ||
        historyJobsRef.current.find((j) => j.id === jobId);
      const applied = {
        ...(known ?? { id: jobId, type: "motion-director" }),
        approved: true,
        status: "approved",
        updatedAt: new Date().toISOString(),
      } as Job;
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
      setHistoryJobs((prev) =>
        prev.some((j) => j.id === jobId)
          ? prev.map((j) => (j.id === jobId ? applied : j))
          : [applied, ...prev]
      );
      emitJobsSettled([jobId], "approved");
      if (!token) return;
      api
        .post(`/${jobId}/approve`)
        .then(() => refetchHistory())
        .catch((err) => console.warn("[App] approve after editor apply failed:", err));
    },
    [token, refetchHistory]
  );

  useEffect(() => {
    const onToast = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      pushToast(d.message, d.kind);
    };
    const onApplied = (e: Event) => markApplied(((e as CustomEvent).detail || {}).jobId);
    window.addEventListener("aidea:toast", onToast);
    window.addEventListener("aidea:jobApplied", onApplied);
    return () => {
      window.removeEventListener("aidea:toast", onToast);
      window.removeEventListener("aidea:jobApplied", onApplied);
    };
  }, [pushToast, markApplied]);

  const handleDecline = useCallback(
    async (jobId: string) => {
      const now = new Date().toISOString();
      let removed: Job | undefined;
      setJobs((prev) => {
        removed = prev.find((j) => j.id === jobId);
        return prev.filter((j) => j.id !== jobId);
      });
      if (removed) {
        const declinedJob: Job = { ...removed, deletedAt: now, updatedAt: now, status: "declined" };
        setHistoryJobs((prev) => [declinedJob, ...prev.filter((j) => j.id !== jobId)]);
      }
      emitJobsSettled([jobId]);

      if (!token) return;
      try {
        await api.post(`/${jobId}/decline`);
        await refetchHistory();
        await fetchAllHistory();
      } catch (err) {
        console.warn("[App] decline API failed (mock mode):", err);
      }
    },
    [token, refetchHistory, fetchAllHistory]
  );

  const handleDismiss = useCallback(
    async (jobId: string) => {
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
      setHistoryJobs((prev) => prev.filter((j) => j.id !== jobId));
      emitJobsSettled([jobId]);

      if (!token) return;
      try {
        await api.post(`/${jobId}/dismiss`);
        await refetchHistory();
        await fetchAllHistory();
      } catch (err) {
        console.warn("[App] dismiss API failed (mock mode):", err);
      }
    },
    [token, refetchHistory, fetchAllHistory]
  );

  // subtitle-generation joblar widgetın hiçbir yerinde gösterilmez
  const isHiddenJob = (job: Job) =>
    job.type === "subtitle-generation" || job.type === "subtitle_generation";
  const visibleJobs = useMemo(() => jobs.filter((j) => !isHiddenJob(j)), [jobs]);
  const visibleHistoryJobs = useMemo(
    () => historyJobs.filter((j) => !isHiddenJob(j)),
    [historyJobs]
  );

  return (
    <HostBridgeContext.Provider value={{ onJobApproved, onAddToScene }}>
      <JobWidget
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        openSignal={openSignal}
        hideFab={hideFab}
        jobs={visibleJobs}
        historyJobs={visibleHistoryJobs}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onDismiss={handleDismiss}
        onResetMockJobs={mockMode ? resetMockJobs : undefined}
      />
      {toasts.length > 0 && (
        <div
          className="fixed bottom-20 right-4 z-50 flex flex-col items-end gap-2 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          {toasts.map((x) => (
            <div
              key={x.id}
              className={`pointer-events-auto max-w-xs rounded-lg border bg-white px-3 py-2 text-[13px] font-medium shadow-lg ${
                x.kind === "error"
                  ? "border-red-200 text-red-700"
                  : "border-purple-200 text-gray-800"
              }`}
            >
              {x.text}
            </div>
          ))}
        </div>
      )}
    </HostBridgeContext.Provider>
  );
}
